import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import {
  Button,
  InputField,
  Icon,
  SingleEntryForm,
  HorizontalSeparator,
  Toggle,
  ToggleBlock,
  ErrorMessage,
} from 'anf-core-react';

import {
  useLazyQuery,
} from '@apollo/client';

import GoogleMapReact from 'google-map-react';
import {
  useOutletContext,
} from 'react-router';

import GET_DROPOFF_LOCATIONS_QUERY from '../../gql/drop-off-location-search.gql';

import DropOffLocationMap from '../../components/drop-off-location-map/drop-off-location-map';
import MapMarker from '../../components/map-marker/map-marker';
import DropOffLocationCard from '../../components/drop-off-location-card/drop-off-location-card';
import useGeocoder from '../../hooks/useGeocoder';
import $window from '../../utils/window';

import { mapMarkerPropsBuilder } from '../../utils/drop-off-location-helper';
import TmntText from '../../components/tmnt-text/tmnt-text';

const resultsViewTypes = {
  list: 'LIST',
  map: 'MAP',
};

export default function DropOffLocations() {
  const {
    orderDetail,
    selectedShippingMethod,
    setSelectedShippingMethod,
    currentPostalCode,
    setCurrentPostalCode,
    mapApiKey,
  } = useOutletContext();

  const {
    country,
    postalCode,
  } = orderDetail.originalOrder.orderDetails.shippingAddress;

  const qrcodeEnabled = !!selectedShippingMethod?.dropoffLocationsResponse?.qrcodeEnabled;

  const [searchInputValue, setSearchInputValue] = useState(currentPostalCode);
  const [searchValueUI, setSearchValueUI] = useState(currentPostalCode);
  const [resultsView, setResultsView] = useState(resultsViewTypes.list);
  const [isInfoWindowOpen, setIsInfoWindowOpen] = useState(false);
  const [infoWindowLocation, setInfoWindowLocation] = useState(undefined);
  const [isInputSearching, setIsInputSearching] = useState(false);
  const [isNearMeSearching, setIsNearMeSearching] = useState(false);
  const [defaultDropOffLocationMapCenter, setDefaultDropOffLocationMapCenter] = useState(null);
  const [isErrorState, setIsErrorState] = useState(false);
  const [isGeolocationBlocked, setIsGeolocationBlocked] = useState(false);
  const geocoder = useGeocoder({ apiKey: mapApiKey });

  const mapComponentProps = {
    defaultDropOffLocationMapCenter,
    mapApiKey,
    mapOptions: {
      defaultZoom: 11,
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      clickableIcons: false,
    },
  };
  const {
    cityStateText,
    findStoreText,
    locationText,
    youText,
    noStoresFoundNearText,
    pleaseTryDifferentLocationText,
    locationServicesBlockedText,
  } = selectedShippingMethod?.dropoffLocationsResponse;

  const orText = TmntText({ k: 'GLB_OR', textOnly: true });

  const handleInputChange = useCallback((inputValue) => {
    setSearchInputValue(inputValue);
  }, []);

  const handleMarkerClick = (location) => {
    setInfoWindowLocation(location);
    setIsInfoWindowOpen(true);
  };

  const [fetchDropoffLocations, {
    data: locationsData,
  }] = useLazyQuery(GET_DROPOFF_LOCATIONS_QUERY, {
    onCompleted: () => {
      setIsInputSearching(false);
      setIsNearMeSearching(false);
    },
  });

  const handleInfoWindowClose = useCallback(() => {
    setIsInfoWindowOpen(false);
  }, []);

  const searchForDropoffLocations = useCallback(async (value) => {
    // before searching clear the values and reset the error state
    setIsErrorState(false);
    setIsGeolocationBlocked(false);

    let geolocation;
    try {
      if (!value) {
        geolocation = await geocoder.findMe();
      } else {
        geolocation = await geocoder.getAddressGeolocation(value);
      }

      setCurrentPostalCode(geolocation.postalCode);
      setDefaultDropOffLocationMapCenter({
        latitude: geolocation.latlng[0],
        longitude: geolocation.latlng[1],
      });
      fetchDropoffLocations({
        variables: {
          dropoffLocationsInput: {
            lat: geolocation.latlng[0].toString(),
            lng: geolocation.latlng[1].toString(),
            country,
            postalCode: geolocation.postalCode,
            nearMe: !value,
            qrcodeEnabled,
            carrierName: selectedShippingMethod?.name,
          },
        },
      });
    } catch (searchError) {
      setIsInputSearching(false);
      setIsNearMeSearching(false);
      if (searchError instanceof $window.GeolocationPositionError || searchError.message === 'geoposition-error') {
        setIsGeolocationBlocked(true);
      } else {
        setIsErrorState(true);
      }
    }
  }, [
    fetchDropoffLocations,
    geocoder,
    country,
    setCurrentPostalCode,
    setDefaultDropOffLocationMapCenter,
    qrcodeEnabled,
    selectedShippingMethod,
  ]);

  const handleInputSearch = useCallback((value) => {
    setIsInputSearching(true);
    searchForDropoffLocations(value);
  }, [searchForDropoffLocations]);

  const handleNearMeSearch = useCallback(() => {
    setIsNearMeSearching(true);
    searchForDropoffLocations();
  }, [searchForDropoffLocations]);

  const inputSearch = useCallback((e) => {
    e.preventDefault();
    setIsInfoWindowOpen(false);
    setSearchValueUI(searchInputValue);
    handleInputSearch(searchInputValue);
  }, [searchInputValue]);

  const nearMeSearch = useCallback((e) => {
    e.preventDefault();
    setSearchValueUI(undefined);
    handleNearMeSearch();
  }, []);

  function createDropOffLocationCards(locations) {
    return locations.map((location, i) => {
      const key = `store-${i}`;
      return (
        <li key={key}>
          <div className="drop-off-locations-list-card-wrapper">
            <DropOffLocationCard
              cardType="list"
              dropOffLocation={location}
            />
          </div>
        </li>
      );
    });
  }

  const infoWindowUI = useMemo(() => {
    if (!infoWindowLocation) {
      return null;
    }
    return (
      <div className="drop-off-locations-info-window-wrapper">
        <DropOffLocationCard
          cardType="info-window"
          dropOffLocation={infoWindowLocation}
          onInfoWindowClose={handleInfoWindowClose}
        />
      </div>
    );
  }, [
    infoWindowLocation,
    handleInfoWindowClose,
  ]);

  const searchInput = useMemo(() => (
    <div className="drop-off-locations-search-input">
      <SingleEntryForm>
        <InputField
          autoComplete="off"
          id="drop-off-locations-dropoff-search"
          label={cityStateText}
          isInvalid={false}
          isRequired
          onChange={(e) => handleInputChange(e.target.value)}
          value={searchInputValue}
        >
          <Button
            classList="drop-off-locations-search-button"
            variant="secondary"
            onClick={(e) => inputSearch(e)}
            isDisabled={isInputSearching || isNearMeSearching}
            isProcessing={isInputSearching}
          >
            {!isInputSearching && (
              <Icon
                icon="search"
                labelText="search"
              />
            )}
          </Button>
        </InputField>
      </SingleEntryForm>
    </div>
  ), [
    searchInputValue,
    cityStateText,
    isInputSearching,
    isNearMeSearching,
    handleInputChange,
    inputSearch,
  ]);

  const nearMe = useMemo(() => (
    <div className="nearme-wrapper">
      <Button
        classList="nearme-button"
        variant="secondary"
        isFullWidth
        onClick={(e) => nearMeSearch(e)}
        isDisabled={isInputSearching || isNearMeSearching || isGeolocationBlocked}
        isProcessing={isNearMeSearching}
      >
        {!isNearMeSearching && (
          <Icon
            icon="location-anf"
            labelText="location"
          />
        )}
        <div
          className="nearme-button-text"
          data-testid="nearme-button-text"
        >
          <TmntText k="GLB_NEAR_ME" />
        </div>
      </Button>
    </div>
  ), [isGeolocationBlocked, isInputSearching, isNearMeSearching, nearMeSearch]);

  const orSeparator = (
    <div className="horizontal-separator-wrapper">
      <HorizontalSeparator>
        {orText}
      </HorizontalSeparator>
    </div>
  );

  const toggleBlock = useMemo(() => (
    <Toggle
      border="round"
      classList="toggle-block"
      // NOTE: we may want to create a tmnt for this
      labelText="Drop-off Option"
    >
      <ToggleBlock
        id="drop-off-locations-list"
        name="list"
        value="list"
        isChecked={resultsView === resultsViewTypes.list}
        onChange={() => setResultsView(resultsViewTypes.list)}
      >
        <TmntText k="SHIPTYPE_SEEUPSACPRESULTLIST" />
      </ToggleBlock>
      <ToggleBlock
        id="drop-off-locations-map"
        isChecked={resultsView === resultsViewTypes.map}
        name="map"
        value="map"
        onChange={() => setResultsView(resultsViewTypes.map)}
      >
        <TmntText k="SHIPTYPE_SEEUPSACPMAP" />
      </ToggleBlock>
    </Toggle>
  ), [resultsView, setResultsView]);

  useEffect(() => {
    const dropoffLocations = selectedShippingMethod?.dropoffLocationsResponse?.dropoffLocations;
    if (dropoffLocations) {
      setDefaultDropOffLocationMapCenter(
        selectedShippingMethod?.dropoffLocationsResponse?.addressGeoCoordinates,
      );
      setCurrentPostalCode(selectedShippingMethod?.dropoffLocationsResponse?.postalCode);
    }
  }, [selectedShippingMethod]);

  useEffect(() => {
    if (locationsData) {
      setIsInputSearching(false);
      setIsNearMeSearching(false);
      setDefaultDropOffLocationMapCenter(locationsData.dropoffLocationSearch?.addressGeoCoordinates);
      // NOTE: the following call will trigger a useEffect in exchange-shipping-method
      // which will essentially merge the data in the selectedShippingMethod into the carriers
      setSelectedShippingMethod((currSelectedShippingMethod) => ({
        ...currSelectedShippingMethod,
        dropoffLocationsResponse: {
          ...locationsData.dropoffLocationSearch,
        },
      }));
    }
    if (locationsData && !locationsData?.dropoffLocationSearch?.addressGeoCoordinates) {
      setIsInputSearching(false);
      setIsNearMeSearching(false);
      setIsErrorState(true);
    }
  }, [locationsData]);

  useEffect(() => {
    if (currentPostalCode) {
      setSearchInputValue(currentPostalCode);
      setSearchValueUI(currentPostalCode);
    }
  }, [currentPostalCode]);

  useEffect(() => {
    if (currentPostalCode) return;
    setCurrentPostalCode(postalCode);
  }, [postalCode]);

  const postalCodeValue = (
    locationsData?.dropoffLocationSearch?.postalCode
    || currentPostalCode
  );

  return (
    <div className="drop-off-locations-wrapper" data-testid="drop-off-locations">
      <h2><TmntText k={selectedShippingMethod?.dropoffLocationsResponse?.headerTextKey} /></h2>
      <div className="header-message">
        <p>
          <TmntText k={selectedShippingMethod?.dropoffLocationsResponse?.subTitleTextKey} />
        </p>
      </div>

      <div className="drop-off-locations-search-wrapper" data-testid="drop-off-locations-search">
        {searchInput}
        {orSeparator}
        {nearMe}
      </div>

      <hr className="drop-off-locations-hr" />

      {(isInputSearching || isNearMeSearching) && (
        <div className="drop-off-locations-view-controls">
          <div className="locations-near-text">
            {`${findStoreText} ${postalCodeValue || youText}`}
          </div>
          <div className="drop-off-locations-toggle">{toggleBlock}</div>
        </div>
      )}

      {selectedShippingMethod?.dropoffLocationsResponse?.dropoffLocations?.length > 0
        && !isErrorState
        && !isInputSearching
        && !isNearMeSearching
      && (
      <>
        <div className="drop-off-locations-view-controls">
          <div className="locations-near-text">
            <p className="location-count">{selectedShippingMethod.dropoffLocationsResponse.dropoffLocations.length}</p>
            <p className="capitalize">{`${locationText} ${postalCodeValue}`}</p>
          </div>
          <div className="drop-off-locations-toggle">{toggleBlock}</div>
        </div>

          {(resultsView === resultsViewTypes.list) && (
            <ul className="drop-off-locations-list-wrapper" data-testid="drop-off-locations-list">
              {createDropOffLocationCards(
                selectedShippingMethod.dropoffLocationsResponse.dropoffLocations,
              )}
            </ul>
          )}

          {(resultsView === resultsViewTypes.map
            && defaultDropOffLocationMapCenter?.latitude
            && selectedShippingMethod?.dropoffLocationsResponse?.dropoffLocations?.length > 0
          ) && (
            <div className="drop-off-locations-map-wrapper" data-testid="drop-off-locations-map">
              <div style={{
                position: 'relative',
                maxHeight: '600px',
                height: '100vh',
                width: '100%',
              }}
              >
                <DropOffLocationMap
                  dropOffLocations={
                    selectedShippingMethod?.dropoffLocationsResponse?.dropoffLocations
                  }
                  MapComponent={GoogleMapReact}
                  MapMarkerComponent={MapMarker}
                  mapComponentProps={mapComponentProps}
                  mapMarkerPropsBuilderMethod={mapMarkerPropsBuilder}
                  onMarkerClick={handleMarkerClick}
                />
              </div>
              {(isInfoWindowOpen && infoWindowUI) && infoWindowUI}
            </div>
          )}
      </>
      )}
      {(isErrorState && !isInputSearching && !isNearMeSearching) && (
        <p className="no-stores" data-testid="no-stores-found">
          {`${noStoresFoundNearText} ${searchValueUI}. ${pleaseTryDifferentLocationText}`}
        </p>
      )}

      {(isGeolocationBlocked) && (
        <div className="drop-off-locations-error" data-testid="geolocation-blocked">
          <ErrorMessage id="drop-off-locations-geolocation-error">
            {locationServicesBlockedText}
          </ErrorMessage>
        </div>
      )}
    </div>
  );
}
